import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { indexModifierOptions, type ModifierOptionPrice } from '@/lib/lineModifiers'
import type { ModifierGroup, ModifierOption, ModifierOptionOverride, ProductModifierConfig } from '@/types'

/**
 * The POS's view of the modifier configuration (F-MOD-N7).
 *
 * ONE query key for the whole menu, deliberately: idbPersister caches the
 * TanStack cache into IndexedDB, so a single key means the configurator keeps
 * working through an outage. A per-product fetch would be dead offline, which
 * is exactly when a cashier least wants a surprise.
 *
 * Product → group ids arrive ALREADY RESOLVED by the server. Never re-derive
 * the category-inheritance rule here: three clients read this payload and they
 * would drift, the same reasoning that makes staff-roles.ts a single authority.
 */
export const MENU_MODIFIERS_QUERY_KEY = ['menu-modifiers'] as const

export interface MenuModifiers {
  /** Every group with its SHARED option values — for admin-style listing only. */
  groups: ModifierGroup[]
  groupById: Map<string, ModifierGroup>
  /**
   * F-MOD-N13: the options AS SOLD ON THIS PRODUCT — a shared option carries
   * the product's own price / availability when it has an override. There is
   * deliberately no menu-wide price index any more: pricing a line from the
   * shared value is how the screen and the bill would disagree.
   *
   * Stable per product for the life of a catalogue (same reasoning as
   * groupsForProduct).
   */
  optionByIdFor: (productId: string) => ReadonlyMap<string, ModifierOption>
  /** Priced index for resolveLineUnitPrice, for this product. Stable per product. */
  priceIndexFor: (productId: string) => ReadonlyMap<string, ModifierOptionPrice>
  /**
   * productId → the groups that apply, in the order the cashier meets them.
   *
   * ⭐ Returns a STABLE reference for a given catalogue. The configurator resets
   * its state when its `groups` prop changes identity, and the counter behind
   * it re-renders on a 15–60s poll — so handing back a fresh array each call
   * wiped the cashier's half-made selections mid-order. Precomputed once,
   * below, rather than mapped on demand.
   */
  groupsForProduct: (productId: string) => ModifierGroup[]
  /**
   * True when tapping the product must open the configurator.
   *
   * Owner decision (2026-08-20): ONLY when something has to be answered. A
   * latte carrying nothing but optional add-ons still rings up in one tap —
   * the coffee shop taps it hundreds of times a shift and would feel a second
   * tap on every single one. Optional groups are reachable from the cart row
   * instead.
   */
  requiresConfigurator: (productId: string) => boolean
  /** True when the product has ANY groups, required or not. */
  hasOptions: (productId: string) => boolean
}

const EMPTY: ProductModifierConfig = { groups: [], product_groups: {} }

export function buildMenuModifiers(cfg: ProductModifierConfig): MenuModifiers {
  const groups = cfg.groups ?? []
  const groupById = new Map(groups.map((g) => [g.id, g]))
  const sharedOptionById = new Map<string, ModifierOption>()
  const sharedPriced: ModifierOptionPrice[] = []
  for (const g of groups) {
    for (const o of g.options ?? []) {
      sharedOptionById.set(o.id, o)
      sharedPriced.push({ id: o.id, price_delta: o.price_delta, price_mode: g.price_mode })
    }
  }
  const sharedPriceIndex = indexModifierOptions(sharedPriced)
  const productGroups = cfg.product_groups ?? {}
  const overrides = cfg.option_overrides ?? {}

  // A product's view of a group: the same object when nothing on it is
  // overridden (cheap, and identity-stable), else a copy whose options carry
  // the product's own price / availability — exactly the server's rule in
  // handlers.loadOptionRows: override price when set, available only if both.
  const groupForProduct = (g: ModifierGroup, mine: Record<string, ModifierOptionOverride> | undefined): ModifierGroup => {
    if (!mine || !(g.options ?? []).some((o) => mine[o.id])) return g
    return {
      ...g,
      options: (g.options ?? []).map((o) => {
        const ov = mine[o.id]
        if (!ov) return o
        return {
          ...o,
          price_delta: ov.price_delta ?? o.price_delta,
          is_available: o.is_available && (ov.is_available ?? true),
        }
      }),
    }
  }

  // Resolved ONCE per catalogue, so every lookup returns the same array /
  // map instance for the life of that catalogue. See groupsForProduct above.
  const resolved = new Map<string, ModifierGroup[]>()
  const optionIndexByProduct = new Map<string, ReadonlyMap<string, ModifierOption>>()
  const priceIndexByProduct = new Map<string, ReadonlyMap<string, ModifierOptionPrice>>()
  for (const [productId, ids] of Object.entries(productGroups)) {
    const mine = overrides[productId]
    const list = ids
      .map((id) => groupById.get(id))
      .filter((g): g is ModifierGroup => Boolean(g))
      .map((g) => groupForProduct(g, mine))
    resolved.set(productId, list)
    if (mine && Object.keys(mine).length > 0) {
      const opts = new Map(sharedOptionById)
      const priced: ModifierOptionPrice[] = []
      for (const g of list) {
        for (const o of g.options ?? []) {
          opts.set(o.id, o)
          priced.push({ id: o.id, price_delta: o.price_delta, price_mode: g.price_mode })
        }
      }
      const index = new Map(sharedPriceIndex)
      for (const [id, v] of indexModifierOptions(priced)) index.set(id, v)
      optionIndexByProduct.set(productId, opts)
      priceIndexByProduct.set(productId, index)
    }
  }
  // One shared empty array, so "no groups" is also referentially stable.
  const NONE: ModifierGroup[] = []
  const groupsForProduct = (productId: string): ModifierGroup[] =>
    resolved.get(productId) ?? NONE

  return {
    groups,
    groupById,
    optionByIdFor: (productId) => optionIndexByProduct.get(productId) ?? sharedOptionById,
    priceIndexFor: (productId) => priceIndexByProduct.get(productId) ?? sharedPriceIndex,
    groupsForProduct,
    // A replace group is required by construction (the DB CHECK forces
    // min_select = 1), so this one test covers sizes as well as required picks.
    requiresConfigurator: (productId) => groupsForProduct(productId).some((g) => g.min_select >= 1),
    hasOptions: (productId) => groupsForProduct(productId).length > 0,
  }
}

export function useMenuModifiers(): { data: MenuModifiers; isLoading: boolean } {
  const q = useQuery({
    queryKey: MENU_MODIFIERS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.getMenuModifiers()
      if (!res.success) throw new Error(res.message)
      return res.data ?? EMPTY
    },
    // Menu configuration changes when an owner edits it, not on a timer, and
    // the admin screens invalidate this key when they do.
    staleTime: 5 * 60 * 1000,
  })
  // Memoised on the payload: without this the whole index — every Map, every
  // closure, every per-product array — is rebuilt on each render of whatever
  // component holds the hook, which is the counter, which polls.
  const data = useMemo(() => buildMenuModifiers(q.data ?? EMPTY), [q.data])
  return { data, isLoading: q.isLoading }
}
